import { DoDisturbAlt, WarningAmber } from '@mui/icons-material';
import { Box, Tooltip, Typography } from '@mui/material';
import { type ReactElement } from 'react';

import { experteBlockungQuelle } from '../../../../dtos';
import { calendarColors } from '../../../../theme/colorPalette';
import { formatDateRange, formatTimeRangeForDate } from '../../../../utils/dateFormat/dateFormat';
import { type CalendarEvent, isExperteEvent } from '../helperClasses';
import { type InternalCalendarEvent } from '../helperClasses/CalendarEvent';
import { calendarEventStates } from '../helperClasses/CalendarEvent/calendarEventStates';
import { EventButton } from './EventButton';
import { ExternalSAPEvent } from './ExternalSAPEvent';
import { VivaEvent } from './VivaEvent';

type DisplayCalendarEventProps = {
  readonly event: CalendarEvent;
};

const isInternalEvent = (calendarEvent: CalendarEvent): calendarEvent is InternalCalendarEvent => {
  if (isExperteEvent(calendarEvent) && calendarEvent.blockungData.quelle !== experteBlockungQuelle.VAMOS) {
    return false;
  }

  return calendarEvent.blockungData.veranstaltung !== null;
};

const isExternalEvent = (calendarEvent: CalendarEvent): calendarEvent is CalendarEvent =>
  calendarEvent.blockungData.veranstaltung === null;

const renderStateIcon = (event: CalendarEvent): ReactElement | null => {
  switch (event.state) {
    case calendarEventStates.ERROR:
      return <DoDisturbAlt fontSize="small" sx={{ marginRight: 0.5 }} htmlColor={calendarColors.errorIcon} />;
    case calendarEventStates.WARNING:
      return <WarningAmber fontSize="small" sx={{ marginRight: 0.5 }} htmlColor={calendarColors.warningIcon} />;
    default:
      return null;
  }
};

const renderCurrentSelectionEvent = (event: CalendarEvent): ReactElement => {
  let label: string;
  if (event.type === 'experte') {
    label = formatTimeRangeForDate(event.start, event.end);
  } else {
    label = formatDateRange(event.start, event.end);
  }

  const getEventOpacity = (): { opacity: number } => {
    switch (event.state) {
      case calendarEventStates.ERROR:
        return { opacity: 0.5 };
      case calendarEventStates.WARNING:
        return { opacity: 0.75 };
      default:
        return { opacity: 1 };
    }
  };

  const notDefaultState = event.state !== calendarEventStates.DEFAULT;

  return (
    <Tooltip title={notDefaultState ? event.message : undefined} disableInteractive>
      <Box>
        <EventButton sx={{ ...getEventOpacity() }} eventColor={calendarColors.selectedBlockung}>
          {renderStateIcon(event)}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="paragraphSm" color="textPrimaryOnBrand">
              {label}
            </Typography>
          </Box>
        </EventButton>
      </Box>
    </Tooltip>
  );
};

const renderOldSelectionEvent = () => (
  <EventButton eventColor={calendarColors.previousSelection}>
    <Typography variant="paragraphSm" color="textPrimaryOnBrand">
      bisherige Auswahl
    </Typography>
  </EventButton>
);

export const DisplayCalendarEvent = ({ event }: DisplayCalendarEventProps) => {
  if (event.isCurrentSelection) {
    return renderCurrentSelectionEvent(event);
  } else if (event.isOldSelection) {
    return renderOldSelectionEvent();
  } else if (isInternalEvent(event)) {
    return <VivaEvent calendarEvent={event} />;
  } else if (isExternalEvent(event)) {
    return <ExternalSAPEvent calendarEvent={event} />;
  }

  return null;
};
